import React, { useState, useMemo, useEffect } from 'react';

import { Button, Input } from '@@/ui/primitives';
import { LogItem, type LogItemProps } from './log-item';
import { ChevronsDownUpIcon, ChevronsUpDownIcon, SearchCodeIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { LogType } from '../../../types/visualizer';
import { List, RowComponentProps, useDynamicRowHeight, useListRef } from 'react-window';
import { useLogNavigation } from '../../../contexts/log-navigation-context';

/** Only enable search for Flow Logs and Tool & Citations */
const SEARCHABLE_TYPES = [LogType.FLOW_LOG, LogType.TOOL_CITATIONS_LOG];

const useSearchResults = (type: LogType, logs: LogItemProps[]) => {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const { navigateToNode } = useLogNavigation();

  const results = useMemo(() => {
    if (!SEARCHABLE_TYPES.includes(type)) return logs;
    if (!filter.trim()) return logs;

    const searchLower = filter.toLowerCase();
    return logs.filter((log) => {
      const searchableText = [
        log.timestamp,
        log.title,
        log.subtitle,
        log.content,
        log.result,
        JSON.stringify(log.parameters),
        log.identifier
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(searchLower);
    });
  }, [logs, filter, type]);

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpanded(newExpanded);
  };

  const toggleAll = () => {
    if (expanded.size === results.length) {
      setExpanded(new Set());
    } else {
      const allIndices = new Set(results.map((_, index) => index));
      setExpanded(allIndices);
    }
  };

  function handleTitleClick(index: number) {
    navigateToNode({
      type: type,
      index: index
    });
  }

  const decoratedResults = useMemo(() => {
    return results.map((log, index) => ({
      ...log,
      isExpanded: expanded.has(index),
      onTitleClick: () => handleTitleClick(index),
      onToggle: () => toggleItem(index)
    }));
  }, [results, expanded]);

  return {
    results: decoratedResults,
    filter: filter,
    toggleItem: toggleItem,
    toggleAll: toggleAll,
    setFilter: setFilter
  };
};

export const LogViewer = ({
  logs,
  logType,
  emptyMessage = 'No logs available'
}: {
  logs: LogItemProps[];
  logType: LogType;
  emptyMessage?: string;
}) => {
  const { results, setFilter, filter, toggleAll, toggleItem } = useSearchResults(logType, logs);
  const { currentLogTarget, clearLogTarget } = useLogNavigation();
  const listWindowRef = useListRef(null);
  const listWindowRowHeight = useDynamicRowHeight({
    defaultRowHeight: 32
  });

  useEffect(() => {
    if (listWindowRef.current && currentLogTarget) {
      const targetIndex = currentLogTarget.index;

      toggleItem(targetIndex);
      listWindowRef.current.scrollToRow({
        index: targetIndex,
        align: 'start',
        behavior: 'smooth'
      });

      clearLogTarget();
    }
  }, [currentLogTarget]);

  const isEveryItemExpanded = results.every((result) => result.isExpanded);

  return (
    <div className='h-full flex flex-col flex-1'>
      <div className='border-b border-neutral-200 bg-neutral-100'>
        <div className='flex items-center justify-between bg-white'>
          <div className='flex items-center gap-1 flex-1 h-10'>
            <Input
              placeholder={`Search logs... (${results.length} total)`}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={clsx(
                'shadow-none rounded-sm text-sm! bg-transparent border-0!',
                'ring-0! focus:shadow-none',
                'text-neutral-950 placeholder:text-neutral-400',
                'outline-0!',
                'w-full'
              )}
            />
          </div>
          <div className='flex items-center gap-1 p-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={toggleAll}
              className='text-xs! h-6 px-2 gap-2 bg-transparent shadow-none'
              disabled={results.length === 0}
            >
              {isEveryItemExpanded ? (
                <>
                  <ChevronsDownUpIcon className='size-4' />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronsUpDownIcon className='size-4' />
                  Expand
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className='flex-1 min-h-0'>
        {!results.length && (
          <div className={clsx('flex flex-col items-center justify-center h-full p-12', 'text-neutral-500', 'gap-3')}>
            <SearchCodeIcon className='size-12 stroke-1' />
            {filter ? 'No logs match your search' : emptyMessage}
          </div>
        )}
        <List
          listRef={listWindowRef}
          rowCount={results.length}
          rowHeight={listWindowRowHeight}
          overscanCount={40}
          rowComponent={RowComponent}
          rowProps={{
            results
          }}
        />
      </div>
    </div>
  );
};

function RowComponent({
  results,
  index,
  style
}: RowComponentProps<{
  results: LogItemProps[];
}>) {
  const logProps = results[index];

  return (
    <div style={style}>
      <LogItem {...logProps} />
    </div>
  );
}
